import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2, Check, X, RotateCcw } from 'lucide-react';
import { useAdminCustomers, useCreateCustomer, useUpdateCustomer, useAdminSites } from '@/hooks/useAdmin';
import { toast } from 'sonner';

export default function CustomersPage() {
  const navigate = useNavigate();
  const { data: rawCustomers = [], isLoading } = useAdminCustomers();
  const customers = Array.isArray(rawCustomers) ? rawCustomers : [];
  const createCustomer = useCreateCustomer();
  const updateCustomer = useUpdateCustomer();
  const { data: rawSites = [] } = useAdminSites();
  const sites = Array.isArray(rawSites) ? rawSites : [];

  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: '', company: '', site_id: '' });

  const filtered = useMemo(() => {
    return customers
      .filter(c => showInactive || c.is_active)
      .filter(c =>
        `${c.name} ${c.company}`.toLowerCase().includes(search.toLowerCase())
      )
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  }, [customers, search, showInactive]);

  const resetForm = () => setForm({ name: '', company: '', site_id: '' });

  const handleCreate = () => {
    if (!form.name.trim()) return;
    createCustomer.mutate(
      {
        name: form.name.trim(),
        company: form.company.trim() || undefined,
        site_id: form.site_id || undefined,
        is_active: true,
      },
      {
        onSuccess: () => {
          resetForm();
          setModalOpen(false);
          toast.success('Customer created');
        },
        onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to create customer'),
      }
    );
  };

  const siteName = (siteId?: string) => {
    if (!siteId) return '';
    const s = sites.find(s => s.id === siteId);
    return s ? s.name : '';
  };

  return (
    <div className="space-y-6 p-6">
      <PageHeader title="Customers" subtitle="Manage customer accounts for CRM" />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <Input
          placeholder="Search customers..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="max-w-md"
        />
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} id="show-inactive" />
            <Label htmlFor="show-inactive" className="text-sm text-muted-foreground cursor-pointer">Show Inactive</Label>
          </div>
          <Button onClick={() => { resetForm(); setModalOpen(true); }} className="gap-1.5">
            <Plus className="h-4 w-4" /> Add Customer
          </Button>
        </div>
      </div>

      {/* Create Customer Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New Customer</DialogTitle>
            <DialogDescription>Fill in the details to create a new customer account.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="cust-name">Customer Name *</Label>
              <Input id="cust-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Acme Corp" autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cust-company">Company</Label>
              <Input id="cust-company" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} placeholder="Parent company (optional)" />
            </div>
            <div className="space-y-1.5">
              <Label>Site</Label>
              <Select value={form.site_id} onValueChange={v => setForm(f => ({ ...f, site_id: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a site…" />
                </SelectTrigger>
                <SelectContent>
                  {sites.filter(s => s.is_active).map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                  {sites.filter(s => s.is_active).length === 0 && (
                    <div className="px-3 py-2 text-sm text-muted-foreground">No sites available</div>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!form.name.trim() || createCustomer.isPending}>
              {createCustomer.isPending ? 'Creating…' : 'Create Customer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customers List */}
      <Card>
        <CardContent className="p-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="space-y-1">
              {filtered.map(c => (
                <div key={c.id} className={cn("flex items-center justify-between py-2 px-3 rounded-md cursor-pointer", c.is_active ? "hover:bg-muted/50" : "bg-muted/60")} onClick={() => { if (editingId !== c.id) navigate(`/crm/customers/${c.id}`); }}>
                  {editingId === c.id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        className="h-8 text-sm"
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            updateCustomer.mutate({ id: c.id, data: { name: editName.trim() } }, { onSuccess: () => toast.success('Updated') });
                            setEditingId(null);
                          }
                        }}
                        autoFocus
                      />
                      <Button variant="ghost" size="sm" onClick={() => { updateCustomer.mutate({ id: c.id, data: { name: editName.trim() } }, { onSuccess: () => toast.success('Updated') }); setEditingId(null); }} className="h-8 w-8 p-0">
                        <Check className="h-4 w-4 text-status-complete" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(null)} className="h-8 w-8 p-0">
                        <X className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="min-w-0">
                        <span className={`text-sm font-medium ${c.is_active ? 'text-foreground' : 'text-muted-foreground line-through'}`}>{c.name}</span>
                        <span className="ml-2 text-xs text-muted-foreground">{c.company}</span>
                        {c.site_name && <Badge variant="secondary" className="ml-2 text-[10px]">{c.site_name}</Badge>}
                        {(c as any).site_id && !c.site_name && siteName((c as any).site_id) && (
                          <Badge variant="secondary" className="ml-2 text-[10px]">{siteName((c as any).site_id)}</Badge>
                        )}
                        {!c.is_active && <Badge variant="outline" className="ml-2 text-[10px]">Inactive</Badge>}
                      </div>
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="sm" onClick={() => { setEditingId(c.id); setEditName(c.name); }} className="h-8 w-8 p-0">
                          <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => updateCustomer.mutate({ id: c.id, data: { is_active: !c.is_active } })} className="h-8 w-8 p-0">
                          {c.is_active ? <Trash2 className="h-3.5 w-3.5 text-destructive" /> : <RotateCcw className="h-3.5 w-3.5 text-primary" />}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              ))}
              {filtered.length === 0 && !isLoading && (
                <p className="py-4 text-center text-sm text-muted-foreground">No customers found</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
